/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Ring } from '@uiball/loaders';
import Select from 'react-select';
import { getMyStore, createAuction } from '../../../utils/services/user.service';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0B1122;
  padding: 2rem;
  color: white;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.5rem;
  color: white;
  cursor: pointer;
  margin-right: 1rem;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.5rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.75rem;
  border-radius: 0.5rem;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #5ce9ff;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SelectWrapper = styled.div`
  .react-select-container {
    .react-select__control {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.5rem;
      min-height: 42px;
      
      &:hover {
        border-color: rgba(255, 255, 255, 0.2);
      }
    }
    
    .react-select__control--is-focused {
      border-color: #5ce9ff;
      box-shadow: 0 0 0 1px #5ce9ff;
    }
    
    .react-select__single-value,
    .react-select__placeholder {
      color: white;
      font-size: 0.875rem;
    }
    
    .react-select__input-container {
      color: white;
    }
    
    .react-select__menu {
      background: #0B1122;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .react-select__option {
      background: #0B1122;
      color: white;
      font-size: 0.875rem;
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      
      &--is-selected {
        background: #5ce9ff;
        color: black;
      }
    }
  }
`;

const TiersSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const TierTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const AddTierButton = styled.button`
  padding: 0.5rem 1rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  border-radius: 0.5rem;
  color: black;
  font-weight: 500;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const TierItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RemoveButton = styled.button`
  padding: 0.5rem;
  background: #ff3b6b;
  border: none;
  border-radius: 0.5rem;
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: linear-gradient(to right, #00f7d2, #5ce9ff);
  border: none;
  border-radius: 0.5rem;
  color: black;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 0.5rem;
  color: white;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const CreateAuction = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const storeIdFromUrl = searchParams.get('storeId');
  
  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.data?.user?.role || 'viewer';
  const isWholeseller = userRole === 'wholesaler';

  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // Auction form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tiers, setTiers] = useState([
    { minQty: 10, price: 5 },
    { minQty: 100, price: 2 },
    { minQty: 500, price: 1 },
  ]);

  useEffect(() => {
    if (!isWholeseller) {
      toast.error('Only wholesellers can create auctions');
      history.push('/auction');
      return;
    }
    fetchStores();
  }, [isWholeseller, history]);

  const fetchStores = async () => {
    try {
      const response = await getMyStore();
      if (response.data?.result) {
        const storeOptions = response.data.result.map(store => ({
          value: store._id,
          label: store.storeName,
        }));
        setStores(storeOptions);
        
        // If storeId is provided in URL, select it
        if (storeIdFromUrl) {
          const storeFromUrl = storeOptions.find(s => s.value === storeIdFromUrl);
          if (storeFromUrl) {
            setSelectedStore(storeFromUrl);
          }
        } else if (storeOptions.length === 1) {
          setSelectedStore(storeOptions[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const handleAddTier = () => {
    setTiers([...tiers, { minQty: 0, price: 0 }]);
  };

  const handleRemoveTier = (index) => {
    if (tiers.length > 1) {
      setTiers(tiers.filter((_, i) => i !== index));
    } else {
      toast.error('At least one tier is required');
    }
  };

  const handleTierChange = (index, field, value) => {
    const updatedTiers = [...tiers];
    updatedTiers[index] = {
      ...updatedTiers[index],
      [field]: parseFloat(value) || 0,
    };
    // Sort tiers by minQty (ascending)
    updatedTiers.sort((a, b) => a.minQty - b.minQty);
    setTiers(updatedTiers);
  };

  const handleSubmit = async () => {
    // Validation
    if (!title) {
      toast.error('Auction title is required');
      return;
    }
    if (!selectedStore) {
      toast.error('Please select a store');
      return;
    }
    if (!endDate) {
      toast.error('End date is required');
      return;
    }
    if (tiers.length === 0) {
      toast.error('At least one pricing tier is required');
      return;
    }
    if (tiers.some(tier => tier.minQty <= 0 || tier.price <= 0)) {
      toast.error('All tiers must have valid quantity and price');
      return;
    }

    try {
      setIsLoading(true);
      
      // TODO: Replace with actual API call
      // const payload = {
      //   title,
      //   description,
      //   store_id: selectedStore.value,
      //   endDate: new Date(endDate).toISOString(),
      //   tiers: tiers.map(tier => ({
      //     minQty: tier.minQty,
      //     price: tier.price,
      //   })),
      // };
      // const response = await createAuction(payload);
      
      // Mock success for now
      toast.success('Auction created successfully!', {
        position: toast.POSITION.TOP_RIGHT,
      });
      history.push('/auction');
    } catch (error) {
      console.error('Error creating auction:', error);
      toast.error(error.response?.data?.Message || 'Failed to create auction', {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isWholeseller) {
    return null;
  }

  return (
    <PageContainer>
      {isLoading && (
        <LoadingOverlay>
          <Ring size={65} color="#00f7d2" />
        </LoadingOverlay>
      )}

      <Header>
        <BackButton onClick={() => history.push('/auction')}>
          ← Back
        </BackButton>
        <Title>Create Auction</Title>
      </Header>

      <FormContainer>
        <FormGroup>
          <Label>Store *</Label>
          <SelectWrapper>
            <Select
              options={stores}
              value={selectedStore}
              onChange={(option) => setSelectedStore(option)}
              placeholder="Select a store"
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable={false}
            />
          </SelectWrapper>
        </FormGroup>

        <FormGroup>
          <Label>Auction Title *</Label>
          <Input
            type="text"
            placeholder="e.g., Bulk Antibiotics Auction"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            placeholder="Describe your auction..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>End Date *</Label>
          <Input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={new Date().toISOString().slice(0, 16)}
          />
        </FormGroup>

        <TiersSection>
          <TierHeader>
            <TierTitle>Pricing Tiers (Quantity-based Discount)</TierTitle>
            <AddTierButton onClick={handleAddTier}>
              + Add Tier
            </AddTierButton>
          </TierHeader>

          {tiers.map((tier, index) => (
            <TierItem key={index}>
              <div>
                <Label>Minimum Quantity</Label>
                <Input
                  type="number"
                  placeholder="e.g., 10"
                  value={tier.minQty}
                  onChange={(e) => handleTierChange(index, 'minQty', e.target.value)}
                  min="1"
                />
              </div>
              <div>
                <Label>Price per Unit (€)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 5.00"
                  value={tier.price}
                  onChange={(e) => handleTierChange(index, 'price', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              <RemoveButton
                type="button"
                onClick={() => handleRemoveTier(index)}
                disabled={tiers.length === 1}
              >
                Remove
              </RemoveButton>
            </TierItem>
          ))}

          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(92, 233, 255, 0.1)', borderRadius: '0.5rem', fontSize: '0.875rem', color: '#5ce9ff' }}>
            <strong>Example:</strong> If a pharmacy buys 10 units, they pay €5 per unit. 
            If they buy 100 units, they pay €2 per unit. If they buy 500 units, they pay €1 per unit.
          </div>
        </TiersSection>

        <ButtonGroup>
          <SaveButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Auction'}
          </SaveButton>
          <CancelButton onClick={() => history.push('/auction')}>
            Cancel
          </CancelButton>
        </ButtonGroup>
      </FormContainer>
    </PageContainer>
  );
};

export default CreateAuction;

